'use client'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { matchBand, matchBandTextClassForBand } from '@/lib/match-bands'

const RADIUS = 41
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

export type ScoreRingProps = {
  /** Score 0–1. */
  score: number
  /** Rendered size in px (default 96). */
  size?: number
  /** Caption under the numeral (default "match"; pass "" to hide). */
  caption?: string
  className?: string
}

/** Band-colored radial score. Sweeps in on mount; static under reduced motion. */
export function ScoreRing({ score, size = 96, caption = 'match', className }: ScoreRingProps) {
  const clamped = Math.max(0, Math.min(1, score))
  const pct = Math.round(clamped * 100)
  const band = matchBand(clamped)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(id)
  }, [])

  const dash = ((mounted ? pct : 0) / 100) * CIRCUMFERENCE
  const ringClass = matchBandTextClassForBand(band)
  const numeralClass = size >= 96 ? 'text-2xl' : 'text-lg'
  const captionClass = size >= 96 ? 'text-[10px]' : 'text-[9px]'

  return (
    <div
      className={cn('relative', className)}
      style={{ width: size, height: size }}
      role="img"
      aria-label={caption ? `${pct}% ${caption}` : `${pct}%`}
    >
      <svg viewBox="0 0 96 96" width={size} height={size} className="-rotate-90">
        <circle cx="48" cy="48" r={RADIUS} fill="none" strokeWidth="7" className="stroke-muted" />
        <circle
          cx="48"
          cy="48"
          r={RADIUS}
          fill="none"
          strokeWidth="7"
          strokeLinecap="round"
          stroke="currentColor"
          strokeDasharray={`${dash} ${CIRCUMFERENCE}`}
          className={cn(
            ringClass,
            'transition-[stroke-dasharray] duration-700 ease-out motion-reduce:transition-none',
          )}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center px-2">
        <span className={cn(numeralClass, 'font-bold tabular-nums tracking-tight leading-none')}>
          {pct}%
        </span>
        {caption ? (
          <span
            className={cn(
              captionClass,
              'mt-0.5 uppercase tracking-wider text-muted-foreground leading-none',
            )}
          >
            {caption}
          </span>
        ) : null}
      </div>
    </div>
  )
}
